import { CLEAR_TEXT_BTN_ID, SAVE_TEXT_BTN_ID, TextFieldControls } from "@components/common/faro-text-field/text-field-controls";
import { useMediaQueryList } from "@hooks/use-media-query";
import { CircularProgress, FontWeights, TextField, Tooltip } from "@flat-ui";
import { DEFAULT_INPUT_FONT_SIZE, DEFAULT_TITLE_FONT_SIZE } from "@styles/common-styles";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { PALETTE_VARS } from "@styles/palette-vars";
/** Default maximum rows to show for multiline text field. */
const DEFAULT_MAX_ROWS = 3;
const CSS_PROPS = {
  /** Adjusts the font color based on the faro variant used */
  fontColor: {
    main: PALETTE_VARS.primary.main,
    secondary: PALETTE_VARS.text.primary,
    row: PALETTE_VARS.text.primary
  },
  /** Adjusts the font weight based on the faro variant used */
  fontWeight: {
    main: FontWeights.Bold,
    secondary: FontWeights.Regular,
    row: FontWeights.SemiBold
  },
  /** Adjusts the font size for the input based on the faro variant used */
  inputFontSize: {
    main: DEFAULT_INPUT_FONT_SIZE,
    secondary: DEFAULT_INPUT_FONT_SIZE,
    row: DEFAULT_TITLE_FONT_SIZE
  },
  /** Adjusts the font size for the input based on the faro variant used */
  inputHeight: {
    main: undefined,
    secondary: undefined,
    row: "59px"
  },
  // When the text field is in row mode, the hover color should be different.
  boxHoverColorRow: {
    main: undefined,
    secondary: undefined,
    row: PALETTE_VARS.action.hover
  },
  paddingRight: {
    main: "0px",
    secondary: "0px",
    row: "19px"
  },
  editButtonSize: {
    main: "24px",
    secondary: "24px",
    row: "36px"
  },
  editIconSize: {
    main: "16px",
    secondary: "16px",
    row: "24px"
  },
  controlButtonSize: {
    main: "24px",
    secondary: "24px",
    row: "24px"
  },
  controlIconSize: {
    main: "16px",
    secondary: "16px",
    row: "18px"
  }
};

/**
 * General component which shows a text field and basic FARO stylings
 */
export function FaroTextField({
  label,
  ariaLabel,
  initialValue,
  placeholder,
  onConfirmed,
  faroVariant = "secondary",
  fontSize,
  isDisabled = false,
  isFullWidth = true,
  isMultiline = false,
  isReadOnly = true,
  shouldShowTooltip = false,
  tooltipProps: {
    title: tooltipTitle,
    ...restTooltipProps
  } = {},
  maxRows = DEFAULT_MAX_ROWS,
  minInputLength = 0,
  maxInputLength,
  validate = () => true,
  defaultEmptyValue = "",
  sx,
  cursor = undefined,
  isLoading = false,
  shouldShowCharacterCount = false,
  shouldClearOnBlur = true,
  hasError = false,
  helperText,
  onCancel = () => undefined,
  dataTestId
}) {
  const {
    isHoverSupported
  } = useMediaQueryList();
  const [value, setValue] = useState(getDisplayValue(initialValue));
  const [isHovered, setIsHovered] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  // Ref to store a reference to the input element
  const inputRef = useRef(null);

  // If hovering is not possible, set the text field to hovered state to show the edit icon etc.
  useEffect(() => {
    if (!isHoverSupported) {
      setIsHovered(true);
    }
  }, [isHoverSupported, isHovered]);

  // React to changes in case the initial value changes
  useEffect(() => {
    setValue(getDisplayValue(initialValue));
    // Do not include getDisplayValue in the dependency array because it causes unnecessary re-renders.
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue]);

  /** Flag whether the confirmation should be disabled because the input is not valid. */
  const isConfirmDisabled = useMemo(() => {
    if (!isEditMode) {
      return false;
    }
    return value.trim().length < minInputLength || !validate(value);
  }, [isEditMode, value, minInputLength, validate]);

  /** Enters the edit mode and makes sure the input is focused. */
  function enterEditMode() {
    inputRef?.current?.focus();
    setIsEditMode(true);
  }

  /** Leaves the edit mode and makes sure the input is not focused anymore */
  function leaveEditMode() {
    inputRef?.current?.blur();
    setIsEditMode(false);
  }

  /**
   * Gets the actual value to be displayed in the text field. If the value equals defaultEmptyValue
   * it means that the value should be an empty string.
   * E.g. when the value is a dash, make sure when the user clicks on the text field, the dash is
   * immediately removed and the user can start typing in an empty text field.
   */
  function getDisplayValue(value) {
    const str = (value ?? "").toString();
    return str === defaultEmptyValue ? "" : str;
  }
  async function onCheckClicked(event) {
    event.stopPropagation();
    if (!isReadOnly && !isDisabled && onConfirmed) {
      // If the text field does not allow an empty value, use it when confirming the value.
      const returnValue = await onConfirmed(value || defaultEmptyValue);
      // If the return value is undefined, it means that the caller will update the value.
      // Either to reset to the previous value if the update was unsuccessful in the backend
      // or to update the value to the new value if the update was successful.
      setValue(getDisplayValue(returnValue));
    }
    leaveEditMode();
  }

  /**
   * Discards all changes in the dialog and closes the edit mode.
   */
  function closeEditMode() {
    leaveEditMode();
    setValue(getDisplayValue(initialValue));
  }

  /**
   * Triggered when the user clicks outside of the input field.
   * Be aware that some elements of the FaroTextField component might be
   * considered to be outside the input field, like the buttons or the label.
   */
  function onBlur(event) {
    // If the user clicked on either save or clear button, let the buttons handle the next step.
    if ([SAVE_TEXT_BTN_ID, CLEAR_TEXT_BTN_ID].includes(event.relatedTarget?.id ?? "")) {
      if (event.relatedTarget?.id === SAVE_TEXT_BTN_ID && isConfirmDisabled) {
        // If onBlur is triggered, the user clicked on save button and that button was disabled
        // the input would normally lose its focus. To prevent this just call focus on the input.
        inputRef?.current?.focus();
      }
      return;
    }
    // Otherwise we assumed the user clicked outside the dialog and therefore we discard changes
    // based on the shouldClearOnBlur props.
    (shouldClearOnBlur || !value.length) && closeEditMode();
  }
  function onClearClicked(event) {
    event.stopPropagation();
    closeEditMode();
    onCancel();
  }

  /**
   * Triggers when the text field is focused, by a click or from the keyboard. An editable field
   * enters the edit mode, so it shows its controls however it was reached.
   */
  function onFocus() {
    if (!isReadOnly && !isDisabled) {
      setIsEditMode(true);
    }

    // Scroll to last position if the text field has lot of texts
    if (isMultiline && inputRef.current) {
      const length = inputRef.current.value.length;
      inputRef.current.setSelectionRange(length, length);
      inputRef.current.scrollLeft = inputRef.current.scrollWidth;
    }
  }
  return <Tooltip component="div" data-testid={dataTestId} {...restTooltipProps} title={shouldShowTooltip && !isEditMode ? tooltipTitle ?? <var>{value}</var> : ""}>
      <TextField placeholder={placeholder} inputRef={inputRef}
    // flat-ui's field for editing in place: no underline at rest, flat-ui's hover, focus and
    // error underlines, no help text, and a counter shown only while focused.
    variant="inline" label={label}
    // Should show the default empty value, when the input is not in edit mode, e.g. a dash.
    text={isEditMode ? value : value || defaultEmptyValue} fullWidth={isFullWidth} multiline={isMultiline} maxRows={maxRows} onBlur={onBlur} onTextChanged={text => {
      setValue(getDisplayValue(text));
      validate(text);
    }} disabled={isDisabled}
    // Only a read-only field is marked so. An editable one enters the edit mode when focused,
    // and flat-ui draws no hover underline on a read-only field.
    readOnly={isReadOnly} onFocus={onFocus} helpText={helperText}
    // A read-only field never shows the counter, so it reserves no row for it either.
    shouldShowCounter={shouldShowCharacterCount && !isReadOnly}
    // The edit mode outlives the focus when `shouldClearOnBlur` is off, so the counter follows it.
    isEditing={isEditMode} maxCharacterCount={shouldShowCharacterCount ? maxInputLength : undefined}
    // flat-ui shows one message under the field, in the error colour when `error` is set and
    // in the help colour otherwise. `true` keeps the field red when there is nothing to say.
    error={hasError ? helperText ?? true : undefined} inputProps={{
      maxLength: maxInputLength,
      // flat-ui renders the label as a plain sibling of the input, with no `htmlFor` and no
      // generated id to point it at, so the input has to carry its own name.
      "aria-label": typeof label === "string" ? label : ariaLabel
    }} endAdornment={<>
            {!isReadOnly && !isDisabled && <TextFieldControls isHovered={isHovered} isEditMode={isEditMode} isConfirmDisabled={isConfirmDisabled} paddingRight={CSS_PROPS.paddingRight[faroVariant]} editButtonSize={CSS_PROPS.editButtonSize[faroVariant]} editIconSize={CSS_PROPS.editIconSize[faroVariant]} controlButtonSize={CSS_PROPS.controlButtonSize[faroVariant]} controlIconSize={CSS_PROPS.controlIconSize[faroVariant]} onEditClicked={enterEditMode} onCheckClicked={onCheckClicked} onClearClicked={onClearClicked} />}
            {isLoading ? <>
                <span style={{
          marginLeft: "5px",
          fontSize: "10px"
        }}>
                  Changing
                </span>
                <CircularProgress sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginLeft: "5px"
        }} size={14} />
              </> : null}
          </>} InputProps={{
      onClick: () => {
        if (!isReadOnly && !isDisabled) {
          enterEditMode();
        }
      },
      onKeyDown: event => {
        // Avoid that MUI DataGridPro handles the space or arrow keys.
        // https://github.com/mui/mui-x/issues/8171#issuecomment-2386994467
        event.stopPropagation();
      },
      onKeyUp: event => {
        if (event.key === "Enter" && !isConfirmDisabled && !isReadOnly && !isMultiline) {
          void onCheckClicked(event);
        }
        if (event.key === "Escape") {
          closeEditMode();
        }
      },
      onMouseOver: () => setIsHovered(true),
      onMouseLeave: () => setIsHovered(false)
    }} controlStyle={{
      // The label is a sibling of the input in flat-ui rather than a descendant of a field
      // root, so it is reached from the form control instead.
      "& label": {
        color: "text.secondary",
        fontSize: DEFAULT_INPUT_FONT_SIZE,
        cursor: cursor ?? null
      },
      //  Always show underline when the device does not support hover
      borderBottom: !isHoverSupported ? `1px solid ${PALETTE_VARS.divider}` : undefined,
      ...sx
    }} sx={{
      "& input, & textArea": {
        color: CSS_PROPS.fontColor[faroVariant] ?? null,
        fontWeight: CSS_PROPS.fontWeight[faroVariant] ?? null,
        fontSize: fontSize ?? CSS_PROPS.inputFontSize[faroVariant] ?? null,
        height: CSS_PROPS.inputHeight[faroVariant] ?? null,
        cursor: cursor ?? null
      },
      "& input": {
        textOverflow: "ellipsis"
      },
      ...(isMultiline && {
        textarea: {
          textAlign: "justify",
          pr: "10px",
          overflowX: "hidden",
          scrollbarColor: `${PALETTE_VARS.action.disabled} transparent`,
          scrollbarWidth: "thin"
        }
      }),
      "& .MuiInputBase-inputMultiline": {
        // This property is needed for the text area
        fontSize
      },
      // When the text field is in row mode and it is not editing, the hover should show a pointer.
      "& .MuiInputBase-input": {
        cursor: faroVariant === "row" && !isEditMode ? "pointer" : null
      },
      "&:hover": {
        backgroundColor: !isEditMode ? CSS_PROPS.boxHoverColorRow[faroVariant] : undefined
      }
    }} />
    </Tooltip>;
}